import type { useIntl } from 'react-intl';
import { getMinutesSince } from './date';

const formatNOK = new Intl.NumberFormat('nb-NO', { currency: 'NOK', style: 'currency' }).format;
const formatSEK = new Intl.NumberFormat('en-GB', { currency: 'SEK', style: 'currency' }).format;
const formatDKK = new Intl.NumberFormat('en-GB', { currency: 'DKK', style: 'currency' }).format;
const formatGBP = new Intl.NumberFormat('en-GB', { currency: 'GBP', style: 'currency' }).format;

const wholeNumberOptions = { maximumFractionDigits: 0, minimumFractionDigits: 0 } as const;
const formatWholeNOK = new Intl.NumberFormat('nb-NO', { currency: 'NOK', style: 'currency', ...wholeNumberOptions })
  .format;
const formatWholeSEK = new Intl.NumberFormat('en-GB', { currency: 'SEK', style: 'currency', ...wholeNumberOptions })
  .format;
const formatWholeDKK = new Intl.NumberFormat('en-GB', { currency: 'DKK', style: 'currency', ...wholeNumberOptions })
  .format;
const formatWholeGBP = new Intl.NumberFormat('en-GB', { currency: 'GBP', style: 'currency', ...wholeNumberOptions })
  .format;

const formatWholeNoNumber = new Intl.NumberFormat('nb-NO', wholeNumberOptions).format;
const formatWholeEnNumber = new Intl.NumberFormat('en-GB', wholeNumberOptions).format;

export const formatWithCurrency = (amount: number, currency: string): string => {
  switch (currency) {
    case 'NOK': {
      return formatNOK(amount);
    }
    case 'SEK': {
      return formatSEK(amount);
    }
    case 'DKK': {
      return formatDKK(amount);
    }
    case 'GBP': {
      return formatGBP(amount);
    }
    default: {
      throw new Error('Currency not found');
    }
  }
};

export const formatWithCurrencyWithoutDecimals = (amount: number, currency: string): string => {
  switch (currency) {
    case 'NOK': {
      return formatWholeNOK(amount);
    }
    case 'SEK': {
      return formatWholeSEK(amount);
    }
    case 'DKK': {
      return formatWholeDKK(amount);
    }
    case 'GBP': {
      return formatWholeGBP(amount);
    }
    default: {
      throw new Error('Currency not found');
    }
  }
};

export const formatWithoutCurrencyAndDecimals = (amount: number, currency: string): string => {
  if (currency === 'NOK') {
    return formatWholeNoNumber(amount);
  }

  return formatWholeEnNumber(amount);
};

export const formatPriceWithCurrency = (
  eatInAmount: number,
  takeawayAmount: number | undefined,
  priceType: string | undefined,
  currency: string
): string => {
  const getFormatter = (curr: string) => {
    switch (curr) {
      case 'NOK': {
        return formatNOK;
      }
      case 'SEK': {
        return formatSEK;
      }
      case 'DKK': {
        return formatDKK;
      }
      case 'GBP': {
        return formatGBP;
      }
      default: {
        throw new Error('Currency not found');
      }
    }
  };

  const formatter = getFormatter(currency);

  return takeawayAmount === undefined
    ? `${formatter(eatInAmount)} ${getWeightUnit(priceType)}`
    : `${formatter(eatInAmount)} ${getWeightUnit(priceType)} | ${formatter(takeawayAmount)} ${getWeightUnit(
        priceType
      )}`;
};

const getWeightUnit = (priceType: string | undefined) => {
  if (priceType === 'PER_KG') {
    return 'per kg';
  }

  if (priceType === 'PER_HG') {
    return 'per hg';
  }

  if (priceType === 'PER_GRAM') {
    return 'per g';
  }

  return '';
};

export const formatDate = (date: string) => {
  const d = new Date(date);
  return `${ordinal(d.getDate())} of ${d.toLocaleString('en-GB', { month: 'long' })} ${d.getFullYear()}`;
};

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]!);
};

export const formatISO8601Date = (dateString: string): string => {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function relativeTime(date: Date): string {
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);

  if (Math.abs(diffSeconds) < 60) {
    return 'Just now';
  }
  if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  }
  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 7) {
    return rtf.format(diffDays, 'day');
  }
  if (Math.abs(diffDays) < 30) {
    return rtf.format(Math.round(diffDays / 7), 'week');
  }
  if (Math.abs(diffDays) < 365) {
    return rtf.format(Math.round(diffDays / 30), 'month');
  }
  return rtf.format(Math.round(diffDays / 365), 'year');
}

export const formatISO8601DateRelative = (dateString: string): string => {
  const date = new Date(dateString.endsWith('Z') ? dateString : `${dateString}Z`);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const day = date.getDate();
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${relativeTime(date)} · ${day}.${month} ${hours}:${minutes}`;
};

export const formatDateWithMonth = (dateString: string): string => {
  const date = new Date(dateString);

  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'long' });
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}. ${month} ${hours}:${minutes}`;
};

export const isSameDay = (date1: string, date2: string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};

export const formatTimeSinceLastOrder = (ISO8601String: string, intl: ReturnType<typeof useIntl>) => {
  const min = getMinutesSince(ISO8601String);
  if (min < 120) {
    return `${min} ${intl.formatMessage({ id: 'digitalBill.shortenMinute' })}`;
  }
  if (min / 60 > 10) {
    return `9 ${intl.formatMessage({ id: 'digitalBill.shortenHour' })} +`;
  }
  const hours = Math.floor(min / 60);
  const restMin = min % 60;
  return `${hours} ${intl.formatMessage({ id: 'digitalBill.shortenHour' })} ${restMin} ${intl.formatMessage({
    id: 'digitalBill.shortenMinute',
  })}`;
};
